import { apiClient } from "./client";
import { organisationStorage } from "../services/storage";
import type {
  Did,
  Organisation,
  OrganisationsResponse,
  SwitchOrganisationResponse,
  VisibleOrganisationsResponse,
} from "@/types";

interface MessageResponse {
  message: string;
}

/**
 * Get all organisations the authenticated user belongs to
 */
export const getOrganisations = async (): Promise<OrganisationsResponse> => {
  const { data } = await apiClient.get<OrganisationsResponse>("/organisations");
  return data;
};

/**
 * Get organisations that are visible to the authenticated user (by visible-to-domain or by invitation)
 */
export const getVisibleOrganisations =
  async (): Promise<VisibleOrganisationsResponse> => {
    const { data } = await apiClient.get<VisibleOrganisationsResponse>(
      "/visible-organisations"
    );
    return data;
  };

/**
 * Switch to a different organisation
 */
export const switchOrganisation = async (
  organisationId: Did
): Promise<SwitchOrganisationResponse> => {
  const { data } = await apiClient.post<SwitchOrganisationResponse>(
    `/organisations/${organisationId}/switch`
  );

  // Store the selected organisation
  await organisationStorage.setCurrentOrganisation(data.organisation);

  return data;
};

/**
 * Get the currently selected organisation from storage
 */
export const getCurrentOrganisation =
  async (): Promise<Organisation | null> => {
    return await organisationStorage.getCurrentOrganisation();
  };

/**
 * Clear the current organisation from storage
 */
export const clearCurrentOrganisation = async (): Promise<void> => {
  await organisationStorage.clearCurrentOrganisation();
};

/**
 * Accept an invitation to join an organisation
 */
export const acceptInvitation = async (
  invitationId: Did
): Promise<MessageResponse> => {
  const { data } = await apiClient.post<MessageResponse>(
    `/invitations/${invitationId}/accept`
  );
  return data;
};

/**
 * Decline an invitation to join an organisation
 */
export const declineInvitation = async (
  invitationId: Did
): Promise<MessageResponse> => {
  const { data } = await apiClient.post<MessageResponse>(
    `/invitations/${invitationId}/decline`
  );
  return data;
};

/**
 * Request access to an organisation
 */
export const requestAccess = async (
  organisationId: Did,
  message?: string
): Promise<MessageResponse> => {
  const { data } = await apiClient.post<MessageResponse>(
    `/organisations/${organisationId}/request-access`,
    { message }
  );
  return data;
};
